import { TalkButton } from './TalkButton'
import { VoiceDiagnostics } from './VoiceDiagnostics'
import { debugGewuenscht } from '../lib/voiceDiagnostics'
import { useEffect } from 'react'
import { useVoiceSession } from '../hooks/useVoiceSession'
import { useChatStore } from '../state/chatStore'
import { useT } from '../lib/i18n'

/**
 * FR-20..FR-25: Mikrofon an/aus, Sprechen per Tippen, Statusfarben nie allein
 * über Farbe.
 *
 * **Das Mikrofon ist nur offen, wenn der Besucher es ausdrücklich öffnet** —
 * Tippen an, Tippen aus, und sobald K4ia antwortet, geht es von selbst zu. Im
 * Lab steht das Gerät in einem Raum mit Publikum: Ein dauerhaft offenes
 * Mikrofon nimmt auf, was niemand an K4ia gerichtet hat, und man sieht dem
 * Gerät nicht an, wann es zuhört. Der grüne Knopf sagt es.
 */
export function VoiceControl() {
  const { status, errorMessage, start, stop, holdToTalk, releaseToTalk } = useVoiceSession()
  const micConsent = useChatStore((s) => s.consent?.microphone ?? false)
  const micOpen = useChatStore((s) => s.micOpen)
  const voiceAutoStart = useChatStore((s) => s.voiceAutoStart)
  const active = status === 'active' || status === 'connecting'
  const t = useT()

  // Einstieg per NFC-Tag mit „Vorlesen": Der Sprachmodus startet von selbst.
  // Ohne Mikrofon-Freigabe geht das nicht — dann erklärt K4ia im Chat.
  useEffect(() => {
    if (!voiceAutoStart || status !== 'idle') return
    useChatStore.getState().setVoiceAutoStart(false)
    if (micConsent) void start()
    else void useChatStore.getState().flushVoiceEntry('chat')
  }, [voiceAutoStart, status, micConsent, start])

  if (!micConsent) {
    return (
      <p className="px-6 pb-4 text-center text-xs text-text-muted">
        Mikrofonzugriff ist nicht freigegeben — Sprechen ist ausgeschaltet, du kannst aber jederzeit
        per Text schreiben.
      </p>
    )
  }

  return (
    <div className="flex flex-col items-center gap-3 px-4 pb-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => (active ? stop() : void start())}
          aria-pressed={active}
          className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${
            status === 'error'
              ? 'bg-status-error text-white'
              : active
                ? 'bg-status-active text-white'
                : 'bg-surface-2 text-text'
          }`}
        >
          <MicIcon />
          {status === 'connecting' ? t.voiceConnecting : active ? t.voiceStop : t.voiceStart}
        </button>
      </div>

      {active && (
        <>
          <TalkButton offen={micOpen} onStart={holdToTalk} onStop={releaseToTalk} />
          <p className="text-xs text-text-muted">{t.voiceHint}</p>
        </>
      )}

      {errorMessage && (
        <p role="alert" className="text-sm text-status-error">
          {errorMessage} — {t.voiceError}
        </p>
      )}

      {/* Versionsstand am Kiosk: winzig, nur ohne laufendes Gespräch.
          Technische Marke wie im Chat (ChatView), für Vorleser verborgen. */}
      {!active && (
        <p
          aria-hidden="true"
          data-versionsstand
          className="select-none font-mono text-[9px] leading-none text-text-muted opacity-40"
        >
          {__K4IA_VERSION__}
        </p>
      )}

      {debugGewuenscht() && <VoiceDiagnostics status={status} />}
    </div>
  )
}

function MicIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M19 11a7 7 0 0 1-14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}
